/** Domain service module: stores.js — Phase A5 Stores / PDV admin */
import { request } from '../http/request'
import { API_BASE_URL } from './config'

function pageUrl(path, page) {
  return `${API_BASE_URL}${path}?page=${page || 1}`
}

class StoreService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/store/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  get(id, query = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const params = []
        if (query.withProducts) params.push('withProducts=1')
        if (query.withStoreUsers) params.push('withStoreUsers=1')
        if (query.withGroup) params.push('withGroup=1')
        const qs = params.length ? `?${params.join('&')}` : ''
        const _result = await request(`${API_BASE_URL}/store/${id}${qs}`, 'get')
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/store/updateOrCreate`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class ProductCategoryService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/productCategory/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          `${API_BASE_URL}/productCategory/updateOrCreate`,
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class ProductService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/product/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/product/updateOrCreate`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Hard-delete if never sold; else soft-inactivate.
   * Returns { deleted, inactivated, product } from API envelope data.
   */
  delete(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/product/${id}`, 'delete')
        resolve(_result?.data?.data ?? null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class ProductVariantService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/productVariant/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          `${API_BASE_URL}/productVariant/updateOrCreate`,
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class StoreProductService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/storeProduct/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          `${API_BASE_URL}/storeProduct/updateOrCreate`,
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class StockService {
  listBalances(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/stock/listBalances', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  listGroupBalances(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          pageUrl('/stock/listGroupBalances', page),
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  listMovements(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/stock/listMovements', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  listGroupMovements(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          pageUrl('/stock/listGroupMovements', page),
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** @deprecated Free type=in invents store stock — blocked on BE. Prefer groupIn + allocate. */
  move(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/stock/move`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Purchase into group pool (bill + cashier). Does not allocate to a store. */
  groupIn(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/stock/groupIn`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Temporary store claim from group pool (no money movement). */
  allocate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/stock/allocate`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  deallocate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/stock/deallocate`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class StoreUserService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/storeUser/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateOrCreate(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(
          `${API_BASE_URL}/storeUser/updateOrCreate`,
          'post',
          data
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  delete(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/storeUser/${id}`, 'delete')
        resolve(_result?.data?.data ?? true)
      } catch (error) {
        reject(error)
      }
    })
  }
}

class SaleService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/sale/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  get(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/${id}`, 'get')
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Admin create sale (Phase E). Defaults identity_mode=identified on BE.
   * @param {{ store_id:number, items:array, identity_mode?:string, beneficiary_legal_person_id?:number, payer_legal_person_id?:number, payer_phone?:string, channel?:string }} data
   */
  create(data) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/create`, 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Start PIX for open/awaiting sale. Returns { sale, checkout_id, pix }. */
  payPix(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/${id}/pay/pix`, 'post', {})
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Pay later — platform invoice (identified + store.invoice_enabled).
   * @param {number|string} id
   * @param {{ due_date?: string }} [body]
   */
  payInvoice(id, body = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/${id}/pay/invoice`, 'post', body || {})
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Admin refund: paid only; restores group+store stock; invoice → REFUNDED. */
  refund(id) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/${id}/refund`, 'post', {})
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Tickets issued for a paid sale (group-scoped). */
  tickets(saleId) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/sale/${saleId}/tickets`, 'get')
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Resend sale receipt + ticket codes by email (paid only).
   * Optional email override; else BE uses payer/beneficiary notification_email.
   * @param {number|string} id
   * @param {{ email?: string }} [body]
   */
  receiptEmail(id, body = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        const payload = {}
        if (body?.email != null && String(body.email).trim() !== '') {
          payload.email = String(body.email).trim()
        }
        const _result = await request(
          `${API_BASE_URL}/pdv/sales/${id}/receipt/email`,
          'post',
          payload
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Resend sale receipt + ticket codes via WhatsApp (paid only).
   * @param {number|string} id
   * @param {{ phone: string }} body
   */
  receiptWhatsApp(id, body) {
    return new Promise(async (resolve, reject) => {
      try {
        const phone = String(body?.phone || '').trim()
        const _result = await request(
          `${API_BASE_URL}/pdv/sales/${id}/receipt/whatsapp`,
          'post',
          { phone }
        )
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

/** Phase C — admin ticket list + PDV lookup/validate */
class TicketService {
  list(data, page) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(pageUrl('/ticket/list', page), 'post', data)
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Lookup by code (manager/seller of store, group admin). */
  lookup(code) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/pdv/tickets/lookup`, 'post', {
          code: String(code || '').trim(),
        })
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }

  /** Check-in validate (manager / ticket_checker / group admin). */
  validate(code) {
    return new Promise(async (resolve, reject) => {
      try {
        const _result = await request(`${API_BASE_URL}/pdv/tickets/validate`, 'post', {
          code: String(code || '').trim(),
        })
        resolve(_result?.data?.data || null)
      } catch (error) {
        reject(error)
      }
    })
  }
}

export const store = new StoreService()
export const productCategory = new ProductCategoryService()
export const product = new ProductService()
export const productVariant = new ProductVariantService()
export const storeProduct = new StoreProductService()
export const stock = new StockService()
export const storeUser = new StoreUserService()
export const sale = new SaleService()
export const ticket = new TicketService()

export default {
  store,
  productCategory,
  product,
  productVariant,
  storeProduct,
  stock,
  storeUser,
  sale,
}
